import { DeleteOutlined } from '@ant-design/icons';
import { Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { type ColumnBlockDataFragment } from '../../../../api/fragments/ColumnBlockData';
import SingleUploadField from '../../../../components/fields/SingleUploadField';
import TranslatedTextAreaField from '../../../../components/fields/TranslatedTextAreaField';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import { allowedExtensions } from '../../../../utilities/extensions';
import ResponsiveStyledTable from '../../../shared/ResponsiveStyledTable';
import { type ColumnsBlockInner } from '../shared';
import { StyledIcon } from './shared';

type ColumnTableProps = {
    columnTable: ColumnsBlockInner[];
    remove: (index: number) => void;
    name: string;
    disabled?: boolean;
};

type ColumnBlockProps = ColumnBlockDataFragment;

const ColumnTable = ({ columnTable, remove, name, disabled = false }: ColumnTableProps) => {
    const { t } = useTranslation(['webpageDetails']);
    const buttonList = (index: number) => (
        <StyledIcon>
            {columnTable.length > 1 && (
                <DeleteOutlined
                    onClick={() => {
                        remove(index);
                    }}
                    style={{ color: 'var(--ant-primary-color)' }}
                />
            )}
        </StyledIcon>
    );

    return (
        <ResponsiveStyledTable
            dataSource={columnTable}
            pagination={false}
            rowKey={(row, index: number) => `${name}_${index.toString()}`}
        >
            <Table.Column<ColumnBlockProps>
                dataIndex="image"
                render={(value, record, index) => (
                    <SingleUploadField
                        disabled={disabled}
                        extensions={[...allowedExtensions.image, ...allowedExtensions.video]}
                        name={`${name}.[${index}].image`}
                        sizeLimitInMiB={20}
                    />
                )}
                title={t('webpageDetails:fields.column.image.label')}
            />
            <Table.Column<ColumnBlockProps>
                dataIndex="title"
                render={(value, record, index) => (
                    <FormFields.TranslatedInputField disabled={disabled} name={`${name}.[${index}].title`} />
                )}
                title={t('webpageDetails:fields.column.title.label')}
            />
            <Table.Column<ColumnBlockProps>
                dataIndex="content"
                render={(value, record, index) => (
                    <TranslatedTextAreaField disabled={disabled} name={`${name}.[${index}].description`} />
                )}
                title={t('webpageDetails:fields.column.description.label')}
            />
            {!disabled && (
                <Table.Column
                    key="actions"
                    align="center"
                    render={(value, record, index) => buttonList(index)}
                    title={t('webpageDetails:actions.action')}
                    width={100}
                />
            )}
        </ResponsiveStyledTable>
    );
};

export default ColumnTable;
